/** Public pallet page opened from the QR code on the pallet label — no login needed. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Package, Loader2, AlertTriangle, Boxes, Weight, Leaf } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PalletPublicView() {
  const { palletNo } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/pallet-production/public/pallet/${encodeURIComponent(palletNo)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Pallet not found"));
  }, [palletNo]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="pallet-public-error">
      <div><AlertTriangle className="w-10 h-10 mx-auto text-red-500 mb-2" /><div className="font-semibold">{err}</div><div className="text-sm text-zinc-500">{palletNo}</div></div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;

  const n = (v, d = 0) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d });
  const ppwr = data.ppwr_kg || {};
  return (
    <div className="min-h-screen bg-zinc-100 p-3 sm:p-6" data-testid="pallet-public-page">
      <div className="max-w-2xl mx-auto space-y-3">
        <div className="bg-indigo-700 text-white rounded-xl p-4 shadow">
          <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Pallet</div>
          <div className="text-2xl sm:text-3xl font-black font-mono" data-testid="pallet-public-no">{data.pallet_no}</div>
          {data.erp_pallet_no ? (
            <div className="mt-1 inline-flex items-center gap-2 bg-white/15 rounded-md px-2 py-1" data-testid="pallet-public-erp">
              <span className="text-[10px] uppercase tracking-widest opacity-80">ERP pallet no</span>
              <span className="font-mono font-bold text-lg">{data.erp_pallet_no}</span>
            </div>
          ) : (
            <div className="mt-1 text-xs opacity-70" data-testid="pallet-public-erp-none">ERP pallet no: not assigned yet</div>
          )}
          <div className="mt-2 text-lg font-semibold">{data.party || "-"}</div>
          <div className="text-sm opacity-90">PO: {(data.po_numbers || []).join(", ") || "-"} · {data.pallet_date || ""} · <span className="uppercase">{data.status}</span></div>
          <div className="text-xs opacity-80 mt-1">
            {data.pallet_type ? `${data.pallet_type} · ` : ""}
            {data.dims?.length_mm ? `${data.dims.length_mm}×${data.dims.width_mm}×${data.dims.height_mm} mm` : ""}
            {data.pl_number ? ` · PL ${data.pl_number}` : ""}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[["Boxes", n(data.total_boxes), Boxes], ["Pcs", n(data.total_pcs), Package], ["Aprox kg", n(data.gross_weight_kg), Weight]].map(([l, v, Icon]) => (
            <div key={l} className="bg-white rounded-lg p-3 text-center shadow-sm">
              <Icon className="w-4 h-4 mx-auto text-zinc-400" /><div className="text-xl font-bold">{v}</div><div className="text-[11px] text-zinc-500 uppercase">{l}</div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg p-3 shadow-sm text-sm flex flex-wrap gap-x-4 gap-y-1 items-center" data-testid="pallet-public-ppwr">
          <span className="font-semibold flex items-center gap-1 text-emerald-700"><Leaf className="w-4 h-4" /> PPWR packaging (approx)</span>
          <span>Paper <b>{n(ppwr.paper, 2)} kg</b></span><span>Plastic <b>{n(ppwr.plastic, 2)} kg</b></span><span>Wood <b>{n(ppwr.wood, 2)} kg</b></span>
        </div>
        <div className="space-y-2">
          {(data.articles || []).map((a, i) => (
            <div key={i} className="bg-white rounded-lg p-3 shadow-sm flex gap-3" data-testid={`pallet-public-article-${a.article_no}`}>
              {a.image_url ? (
                <img src={a.image_url} alt={a.article_no} className="w-24 h-24 object-contain bg-white rounded border shrink-0" />
              ) : (
                <div className="w-24 h-24 rounded border bg-zinc-50 flex items-center justify-center text-[10px] text-zinc-400 shrink-0">no picture</div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-mono font-bold text-lg">{a.article_no}</div>
                <div className="text-sm text-zinc-700">{a.description}</div>
                <div className="text-xs text-zinc-500 mt-1">PO {a.po_number || "-"}</div>
                <div className="flex gap-4 mt-1 text-sm">
                  <span><b>{n(a.no_of_boxes)}</b> boxes</span><span><b>{n(a.pcs_per_box)}</b> pcs/box</span><span><b>{n(a.total_pcs)}</b> pcs</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
